import { Tooltip } from "@mantine/core";
import ELK from "elkjs/lib/elk.bundled";
import { useEffect, useMemo, useState } from "react";
import { defaultNodeConfigs, getNodeDetails } from "../flowSettings";
import { useFlowStore } from "../../../../../../stores/WorkflowStore";

const WorkflowPreview = () => {
  const steps = useMemo(() => {
    const allNodes = useFlowStore.getState().nodes;
    let initialId = useFlowStore.getState().initialId;

    let needDummy = allNodes.find(
      (x) => x?.data?.source === initialId && x?.data?.actionType === "event"
    );

    let nodes = allNodes.filter((x) => {
      if (needDummy) {
        return x.type === "action" || x.type === "start";
      } else {
        return x.type === "action";
      }
    });

    return nodes.map((step, index) =>
      step?.data?.actionType !== "event"
        ? {
            step_id: {
              type: step?.data?.exist ? "existing" : "new",
              id: step.id === "0" ? 1 : parseInt(step.id),
            },
            step_type: "action",
            position_x: step.x,
            position_y: step.y,
            parent_ids:
              step?.data?.source === initialId || step.type === "start"
                ? []
                : Array.isArray(step?.data?.source)
                ? step?.data?.source.map((s) => ({
                    type: nodes.find((x) => x.id === s)?.data?.exist
                      ? "existing"
                      : "new",
                    id: parseInt(s),
                  }))
                : [
                    {
                      type: nodes.find((x) => x.id === step.data.source)?.data
                        ?.exist
                        ? "existing"
                        : "new",
                      id:
                        step.data.source === "0"
                          ? 1
                          : parseInt(step.data.source),
                    },
                  ],
            action_type: step.data.actionType,
            model_id: step.data?.model_id,
            integration_id: step.data?.integration_id,
            pool_id: step.data?.pool_id,
            is_initial: needDummy
              ? step.type === "start"
              : step.data?.source === initialId,
          }
        : {
            step_id: {
              type: step?.data?.exist ? "existing" : "new",
              id: parseInt(step.id),
            },
            step_type: "event",
            event_type: step.data?.event_type,
            position_x: step.x,
            position_y: step.y,
            filter: step.data?.filter,
            which_links_clicked: step.data?.which_links_clicked,
            when_minutes:
              step.data?.when_minutes || defaultNodeConfigs?.when_minutes,
            name: step.data?.name,
            parent_ids: [
              {
                type: nodes.find((x) => x.id === step.data.source)?.data?.exist
                  ? "existing"
                  : "new",
                id: step.data.source === "0" ? 1 : parseInt(step.data.source),
              },
            ],
          }
    );
  }, []);

  const [layout, setLayout] = useState(null);

  useEffect(() => {
    const elk = new ELK();
    const runLayout = async () => {
      const elkNodes = steps.map((step) => ({
        id: step.step_id.id.toString(),
        width: 64,
        height: 64,
      }));

      const elkEdges = steps.flatMap((step) =>
        (step.parent_ids || []).map((parent) => ({
          id: `e${parent.id}-${step.step_id.id}`,
          sources: [parent.id.toString()],
          targets: [step.step_id.id.toString()],
        }))
      );

      const graph = {
        id: "root",
        layoutOptions: {
          "elk.algorithm": "mrtree",
          "elk.direction": "DOWN",
          "elk.spacing.nodeNode": "48",
        },
        children: elkNodes,
        edges: elkEdges,
      };

      const layoutResult = await elk.layout(graph);
      setLayout(layoutResult);
    };

    runLayout();
  }, [steps]);

  if (!layout) return <p>...</p>;

  const padding = 0;
  const viewBoxWidth = layout.width + padding * 2;
  const viewBoxHeight = layout.height + padding * 2;

  const stepMap = Object.fromEntries(
    steps.map((s) => [s.step_id.id.toString(), s])
  );

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${viewBoxWidth + 48} ${viewBoxHeight + 48}`}
      preserveAspectRatio="xMidYMid meet"
      id="workflow-preview"
    >
      <defs>
        <marker
          id="arrow"
          markerWidth="10"
          markerHeight="10"
          refX="6"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="gray" />
        </marker>
      </defs>

      {/* Draw edges */}
      {layout.edges?.map((edge) =>
        edge.sections?.map((section, i) => (
          <line
            key={`${edge.id}-seg-${i}`}
            x1={section.startPoint.x + padding}
            y1={section.startPoint.y + padding - 12}
            x2={section.endPoint.x + padding}
            y2={section.endPoint.y + padding + 8}
            stroke="gray"
            strokeWidth={2}
          />
        ))
      )}

      {/* Draw nodes */}
      {layout.children?.map((node) => {
        const step = stepMap[node.id];

        const x = node.x + padding;
        const y = node.y + padding;

        const nodeDetails = getNodeDetails(
          step?.action_type || step?.step_type
        );

        const icon = getNodeDetails(step?.action_type || step.step_type, {
          x: x + node.width / 4,
          y: y + node.height / 4,
          size: 32,
          color: nodeDetails.color,
        })?.icon;

        return (
          <Tooltip key={node.id} label={nodeDetails.label}>
            <g key={node.id}>
              <title>{nodeDetails.label}</title>
              <rect
                x={x}
                y={y}
                width={node.width}
                height={node.height}
                rx={10}
                ry={10}
                fill="#fff"
                stroke={nodeDetails.color + "99"}
              />
              {icon}
            </g>
          </Tooltip>
        );
      })}
    </svg>
  );
};

export default WorkflowPreview;
